import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  PencilSquare,
  Key,
  Envelope,
  Telephone,
  GeoAlt,
  ClockHistory,
  PersonCircle,
  Clipboard,
} from "react-bootstrap-icons";
import "./styles/view-details.css";
import { useAuth } from "../../contexts/AuthContext";

export default function ViewDetails() {
  const auth = useAuth();
  const USER_API = process.env.REACT_APP_USER_SERVICE_URL;
  const navigate = useNavigate();
  const abortRef = useRef(null);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copyStatus, setCopyStatus] = useState("");

  useEffect(() => {
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;
    const token = localStorage.getItem("authToken");

    setLoading(true);
    setError("");

    (async () => {
      try {
        const res = await fetch(`${USER_API}/sellers/me`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        });

        const contentType = res.headers.get("content-type") || "";
        const text = await res.text();

        //  Prevent HTML crash
        if (
          contentType.includes("text/html") ||
          text.trim().startsWith("<!DOCTYPE")
        ) {
          console.error("HTML response:", text.slice(0, 300));
          throw new Error("Server returned HTML instead of JSON");
        }

        const data = text ? JSON.parse(text) : {};

        if (!res.ok) {
          throw new Error(data.message || `Error ${res.status}`);
        }

        // Map backend → frontend
        setProfile({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          city: data.city,
          country: data.country,
          avatarUrl: data.avatarUrl,
          updatedAt: data.updatedAt,
          createdAt: data.createdAt,
        });
      } catch (err) {
        if (err.name === "AbortError") return;

        console.error(err);
        setError(err.message || "Failed to load profile");
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const onCopyEmail = useCallback(async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      setCopyStatus("Copied!");
      setTimeout(() => setCopyStatus(""), 2000);
    } catch {
      setCopyStatus("Copy failed");
      setTimeout(() => setCopyStatus(""), 2000);
    }
  }, []);

  const initials = profile
    ? `${profile.firstName?.[0] || ""}${profile.lastName?.[0] || ""}`.toUpperCase()
    : "";

  return (
    <div className="view-details">
      <div className="d-flex align-items-start justify-content-between mb-3">
        <div>
          <h4 className="mb-1">Personal Details</h4>
          <div className="text-muted small">
            Your account information and security settings
          </div>
        </div>

        <div className="d-flex gap-2">
          <Link
            to="edit"
            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
          >
            <PencilSquare /> Edit Profile
          </Link>

          <Link
            to="change-password"
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
          >
            <Key /> Change Password
          </Link>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body p-3 p-md-4">
          {loading ? (
            <div className="row gx-3">
              <div className="col-auto">
                <div className="skeleton-avatar" />
              </div>
              <div className="col">
                <div className="skeleton-line w-50 mb-2" />
                <div className="skeleton-line w-40 mb-3" />
                <div className="skeleton-grid">
                  <div className="skeleton-box" />
                  <div className="skeleton-box" />
                </div>
              </div>
            </div>
          ) : error ? (
            <div role="alert" className="alert alert-danger mb-0">
              {error}
            </div>
          ) : (
            <div className="row gx-4">
              <aside className="col-md-4 col-lg-3 text-center text-md-start mb-3 mb-md-0">
                <div className="profile-avatar mb-3">
                  {profile?.avatarUrl ? (
                    <img
                      src={profile.avatarUrl}
                      alt={`${profile.firstName} ${profile.lastName}`}
                      className="rounded-circle"
                    />
                  ) : (
                    <div className="avatar-fallback rounded-circle">
                      <PersonCircle className="avatar-icon" />
                      <div className="avatar-initials">{initials}</div>
                    </div>
                  )}
                </div>

                <div className="fw-semibold fs-5">
                  {profile?.firstName} {profile?.lastName}
                </div>
                <div className="text-muted small mb-3">
                  Member since{" "}
                  <strong>{new Date(profile?.createdAt).getFullYear()}</strong>
                </div>

                <div className="d-grid gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm d-flex align-items-center justify-content-center gap-2"
                    onClick={() => navigate("edit")}
                  >
                    <PencilSquare /> Edit
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center gap-2"
                    onClick={() => navigate("change-password")}
                  >
                    <Key /> Change Password
                  </button>
                </div>
              </aside>

              <main className="col-md-8 col-lg-9">
                <div className="mb-3">
                  <h6 className="mb-2">Contact</h6>

                  <div className="d-flex align-items-center gap-3 mb-2">
                    <Envelope className="text-muted" />
                    <div>
                      <div className="small text-muted">Email</div>
                      <div className="d-flex align-items-center gap-2">
                        <div className="fw-medium">{profile?.email}</div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => onCopyEmail(profile.email)}
                          aria-label="Copy email"
                          title="Copy email"
                        >
                          <Clipboard />
                        </button>
                        {copyStatus && (
                          <div className="small text-success ms-2">
                            {copyStatus}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-3 mb-2">
                    <Telephone className="text-muted" />
                    <div>
                      <div className="small text-muted">Phone</div>
                      <div className="fw-medium">{profile?.phone}</div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <GeoAlt className="text-muted" />
                    <div>
                      <div className="small text-muted">Location</div>
                      <div className="fw-medium">
                        {profile?.city}, {profile?.country}
                      </div>
                    </div>
                  </div>
                </div>

                <hr />

                <div className="mb-3">
                  <h6 className="mb-2">Security</h6>
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="small text-muted">
                        Last password change
                      </div>
                      <div className="fw-medium">
                        <ClockHistory />{" "}
                        {new Date(profile?.updatedAt).toLocaleString()}
                      </div>
                    </div>

                    <div>
                      <Link
                        to="change-password"
                        className="btn btn-sm btn-outline-primary"
                      >
                        Update
                      </Link>
                    </div>
                  </div>
                </div>

                <hr />

                <div>
                  <h6 className="mb-2">Activity</h6>
                  <div className="small text-muted">
                    Recent sign-ins and device activity are shown here for your
                    security.
                  </div>

                  <div className="mt-3">
                    <div className="activity-item d-flex align-items-start gap-3 mb-2">
                      <div className="activity-dot" />
                      <div>
                        <div className="fw-medium">
                          Signed in from Addis Ababa
                        </div>
                        <div className="small text-muted">
                          Today · {new Date().toLocaleTimeString()}
                        </div>
                      </div>
                    </div>

                    <div className="activity-item d-flex align-items-start gap-3">
                      <div className="activity-dot" />
                      <div>
                        <div className="fw-medium">Password changed</div>
                        <div className="small text-muted">2 months ago</div>
                      </div>
                    </div>
                  </div>
                </div>
              </main>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
